import { useEffect, useMemo, useRef, useState } from "react";
import { Spin } from "antd";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BorderOuterOutlined,
  CloudOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  MenuOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import ProfileDropdown from "../../../components/ProfileDropdown";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useIsEdgeLocal } from "../../../hooks/useEdgeMode";
import { logout, setAccess } from "../../../store/authSlice";
import { denyAccess } from "../../../api/access";
import type { SchemaElement } from "../../../api/restaurantSchema";
import type { LogicalPoint } from "../../../components/restaurant-schema/stageLayout";
import { useRestaurantSchema } from "../../../hooks/useRestaurantSchema";
import type { ModuleOutletContext } from "../../../components/moduleSidebar/ModuleSidebarLayout";
import DeleteConfirmModal from "../../configuration/components/DeleteConfirmModal";
import RegionTabs, { NewRegionButton } from "./RegionTabs";
import EditorToolbar from "./EditorToolbar";
import SchemaCanvas from "./canvas/SchemaCanvas";
import TableInspector from "./panel/TableInspector";
import ObjectInspector from "./panel/ObjectInspector";
import RegionSummary from "./panel/RegionSummary";
import ElementList from "./panel/ElementList";
import TableCreateModal from "./modals/TableCreateModal";
import RegionCreateModal from "./modals/RegionCreateModal";
import RegionSettingsModal from "./modals/RegionSettingsModal";
import { nextTableName } from "../../../components/restaurant-schema/tableNames";
import { useSchemaEditorActions } from "./useSchemaEditorActions";
import { useSchemaHistory } from "./useSchemaHistory";
import { useEditorShortcuts } from "./useEditorShortcuts";

type OpenModal = "regionCreate" | "regionSettings" | "regionDelete" | "tableCreate";

/**
 * Резервации → Схема на заведението (дизайн „Схема на заведението —
 * подобрения"): региони като табове, платно с лента за добавяне, инспектор и
 * списък вдясно, Отмени/Повтори. Същата схема ще се ползва за избор на маса
 * в Маркиране и в резервациите — договорът е в docs/restaurant-schema.md.
 */
const RestaurantSchemaPage = () => {
  const { t } = useTranslation();
  const { isNarrow, onOpenMobileMenu } = useOutletContext<ModuleOutletContext>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const access = useAppSelector((state) => state.auth.access);
  const names = useAppSelector((state) => state.auth.names);
  const username = useAppSelector((state) => state.auth.username);

  // Без интернет заявките отиват към касата, а тя няма схемата (503) —
  // вместо сива грешка казваме защо (docs/edge.md: disabled с подсказка).
  const isEdgeLocal = useIsEdgeLocal();
  const { data: regions = [], isLoading, isError } = useRestaurantSchema();
  // Отваря се на района, от който се идва (празният район в „Резервации" —
  // `SchemaSetupNotice`), иначе на първия.
  const location = useLocation();
  const [activeRegionId, setActiveRegionId] = useState<number | null>(
    () => (location.state as { regionId?: number } | null)?.regionId ?? null,
  );
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [openModal, setOpenModal] = useState<OpenModal | null>(null);
  // Средата на видимата част на платното — там отиват новите маси и обекти.
  const viewCenterRef = useRef<(() => LogicalPoint) | null>(null);

  const region = useMemo(
    () => regions.find((candidate) => candidate.id === activeRegionId) ?? regions[0],
    [regions, activeRegionId],
  );
  const history = useSchemaHistory();
  const actions = useSchemaEditorActions(region, history);
  const suggestedTableName = nextTableName(regions);
  const selected = region?.elements.find((element) => element.id === selectedId);

  // Друг показан регион (таб, нов, изтрит, изчезнал) → нова история: стъпките
  // са заявки по id на елемент и ⌘Z иначе би пипнал невидимия регион.
  const regionId = region?.id;
  const resetHistory = history.reset;
  useEffect(() => {
    resetHistory();
  }, [regionId, resetHistory]);

  // Избраният елемент изчезна (изтрит/отменен/друг регион) → без избор.
  useEffect(() => {
    if (selectedId !== null && !selected) setSelectedId(null);
  }, [selected, selectedId]);

  useEditorShortcuts({
    enabled: openModal === null && !!region,
    hasSelection: !!selected,
    onUndo: history.undo,
    onRedo: history.redo,
    onRotate: () => selected && actions.rotateElement(selected),
    onDuplicate: () => selected && duplicate(selected),
    onDelete: () => selected && void actions.deleteElement(selected),
    onDeselect: () => setSelectedId(null),
  });

  const handleLogout = async () => {
    if (access) {
      try {
        await denyAccess();
      } catch (error) {
        console.error("Deny access error:", error);
      }
      dispatch(setAccess(false));
      navigate("/", { replace: true });
    } else {
      await dispatch(logout());
      navigate("/login", { replace: true });
    }
  };

  const closeModal = () => setOpenModal(null);
  const selectAfterCreate = (created: { id: number } | undefined) => {
    if (created) setSelectedId(created.id);
  };
  // Копието се избира — еднакво от лентата, от панела и с ⌘D.
  const duplicate = (element: SchemaElement) =>
    void actions.duplicateElement(element, suggestedTableName).then(selectAfterCreate);

  const topBar = (
    <div
      className={`flex min-h-12 shrink-0 items-center justify-between gap-2.5 border-b border-shade-2 px-4 py-2.5 sm:min-h-[60px] sm:px-7 sm:py-[11px] ${
        access
          ? "bg-[radial-gradient(ellipse_40%_120%_at_100%_50%,var(--color-brand-tint)_0%,transparent_70%),var(--color-surface)]"
          : "bg-surface"
      }`}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {isNarrow && (
          <button
            type="button"
            onClick={onOpenMobileMenu}
            aria-label="Open menu"
            className="flex h-[34px] w-[34px] shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-[18px] text-ink"
          >
            <MenuOutlined />
          </button>
        )}
        <div className="min-w-0">
          <h1 className="m-0 truncate text-[18px] font-bold leading-[1.2] tracking-[-0.5px] text-ink sm:text-[22px]">
            {t("restaurantSchema.title")}
          </h1>
          {!isNarrow && (
            <div className="mt-1 truncate text-[12px] font-medium text-muted">
              {t("restaurantSchema.subtitle")}
            </div>
          )}
        </div>
      </div>
      <ProfileDropdown
        displayName={names || username || ""}
        username={username ?? ""}
        access={access}
        isNarrow={isNarrow}
        onLogout={handleLogout}
      />
    </div>
  );

  const notice = (tone: "warn" | "danger", icon: React.ReactNode, title: string, hint: string) => (
    <div className="flex items-start gap-3 rounded-[14px] border border-shade-2 bg-surface px-6 py-5">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px] ${
          tone === "warn" ? "bg-warn-tint text-warn-ink" : "bg-danger-tint text-danger-ink"
        }`}
      >
        {icon}
      </span>
      <div>
        <div className="text-[14px] font-bold tracking-[-0.2px] text-ink">{title}</div>
        <div className="mt-0.5 text-[12.5px] text-body">{hint}</div>
      </div>
    </div>
  );

  const renderBody = () => {
    if (isEdgeLocal) {
      return notice("warn", <CloudOutlined />, t("edge.unavailableOffline"), t("restaurantSchema.offlineHint"));
    }
    if (isLoading) {
      return (
        <div className="flex justify-center rounded-[14px] border border-shade-2 bg-surface p-12">
          <Spin size="large" />
        </div>
      );
    }
    if (isError) {
      return notice(
        "danger",
        <ExclamationCircleOutlined />,
        t("restaurantSchema.loadError"),
        t("restaurantSchema.loadErrorHint"),
      );
    }
    if (!region) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-[14px] border border-dashed border-shade-4 bg-surface px-6 py-14 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-[18px] bg-brand-tint text-[30px] text-brand-deep-ink">
            <BorderOuterOutlined />
          </span>
          <div>
            <div className="text-[20px] font-bold leading-[1.2] tracking-[-0.4px] text-ink">
              {t("restaurantSchema.empty.title")}
            </div>
            <div className="mx-auto mt-2 max-w-[440px] text-[13.5px] font-medium leading-[1.5] text-body">
              {t("restaurantSchema.empty.description")}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpenModal("regionCreate")}
            className="flex h-[42px] cursor-pointer items-center gap-2 rounded-[10px] border-none bg-brand px-5 text-[14px] font-bold text-white transition-colors duration-150 hover:bg-brand-deep"
          >
            <PlusOutlined />
            {t("restaurantSchema.empty.cta")}
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
          <div className="min-w-0 lg:flex-1">
            <RegionTabs
              regions={regions}
              activeId={region.id}
              onChange={(nextRegionId) => {
                setActiveRegionId(nextRegionId);
                setSelectedId(null);
              }}
              // На телефон горният ред е само за табовете — „Нов регион" слиза долу.
              onCreate={isNarrow ? undefined : () => setOpenModal("regionCreate")}
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <EditorToolbar
              canUndo={history.canUndo}
              canRedo={history.canRedo}
              onUndo={history.undo}
              onRedo={history.redo}
              onOpenSettings={() => setOpenModal("regionSettings")}
            />
            {isNarrow && <NewRegionButton onClick={() => setOpenModal("regionCreate")} />}
          </div>
        </div>

        <div className="flex flex-col gap-4 lg:grid lg:min-h-0 lg:flex-1 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="flex h-[64dvh] min-h-[380px] lg:h-auto lg:min-h-0">
            <SchemaCanvas
              region={region}
              selectedId={selectedId}
              onSelect={setSelectedId}
              onMove={actions.moveElement}
              onRotate={actions.rotateElementTo}
              onRotateBlocked={actions.warnNoRoom}
              onDuplicate={duplicate}
              onDelete={(element) => void actions.deleteElement(element)}
              onAddTable={() => setOpenModal("tableCreate")}
              onAddObject={(kind, dropPoint) =>
                void actions
                  .createObject(kind, dropPoint, viewCenterRef.current?.())
                  .then(selectAfterCreate, () => undefined)
              }
              viewCenterRef={viewCenterRef}
            />
          </div>
          <aside className="flex flex-col gap-4 lg:min-h-0">
            {selected?.kind === "table" && (
              <TableInspector
                key={selected.id}
                table={selected}
                actions={actions}
                onDuplicate={() => duplicate(selected)}
                onClose={() => setSelectedId(null)}
              />
            )}
            {selected && selected.kind !== "table" && (
              <ObjectInspector
                element={selected}
                actions={actions}
                onDuplicate={() => duplicate(selected)}
                onClose={() => setSelectedId(null)}
              />
            )}
            {!selected && <RegionSummary region={region} />}
            <ElementList
              elements={region.elements}
              selectedId={selectedId}
              onSelect={setSelectedId}
            />
          </aside>
        </div>
      </>
    );
  };

  return (
    <div className="flex h-full flex-col bg-canvas">
      {topBar}
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-3.5 pb-6 pt-3.5 sm:px-7 sm:pb-6 sm:pt-4 lg:overflow-hidden">
        {renderBody()}
      </div>

      {openModal === "regionCreate" && (
        <RegionCreateModal
          open
          onClose={closeModal}
          onSubmit={async (name) => {
            const created = await actions.createRegion(name);
            setActiveRegionId(created.id);
            closeModal();
          }}
        />
      )}

      {openModal === "regionSettings" && region && (
        <RegionSettingsModal
          open
          region={region}
          onClose={closeModal}
          onSave={async (name) => {
            await actions.saveRegion(name);
            closeModal();
          }}
          onDelete={() => setOpenModal("regionDelete")}
        />
      )}

      <DeleteConfirmModal
        open={openModal === "regionDelete"}
        title={t("restaurantSchema.regionDelete.title", { name: region?.name ?? "" })}
        description={t("restaurantSchema.regionDelete.description")}
        confirmLabel={t("restaurantSchema.regionDelete.confirm")}
        cancelLabel={t("common.cancel")}
        icon={<DeleteOutlined className="text-[18px]" />}
        onConfirm={async () => {
          const deleted = await actions
            .deleteRegion()
            .then(() => true)
            .catch(() => false);
          if (deleted) {
            setActiveRegionId(null);
            closeModal();
          } else {
            setOpenModal("regionSettings");
          }
        }}
        // Отказът връща към настройките, от които е дошъл, не затваря всичко.
        onCancel={() => setOpenModal("regionSettings")}
      />

      {openModal === "tableCreate" && (
        <TableCreateModal
          open
          regions={regions}
          suggestedName={suggestedTableName}
          onClose={closeModal}
          onSubmit={async (values) => {
            const created = await actions.createTable(values, viewCenterRef.current?.());
            selectAfterCreate(created);
            return created;
          }}
        />
      )}
    </div>
  );
};

export default RestaurantSchemaPage;
